/**
 * useEssenceReward — Hook for awarding Brand Essence to the Personal God.
 * 
 * Fires non-blocking calls to /api/avatar/absorb-essence when the user
 * completes key actions. Shows toast notifications for essence gains
 * and tier-up events. Detects consciousness milestones (30%/60%/80%).
 */

import { useCallback, useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Debounce map to avoid double-rewarding the same action
const recentRewards = new Map();

export function useEssenceReward() {
  const userId = useRef(null);
  const [pendingMilestone, setPendingMilestone] = useState(null);

  // Resolve user_id lazily from localStorage
  const getUserId = useCallback(() => {
    if (userId.current) return userId.current;
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      userId.current = payload.sub || payload.user_id || null;
      return userId.current;
    } catch { return null; }
  }, []);

  // Check for pending milestones on mount
  useEffect(() => {
    const checkMilestones = async () => {
      const uid = getUserId();
      if (!uid) return;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/avatar/milestones?user_id=${uid}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.milestones && data.milestones.length > 0) {
            // Show the highest-priority undismissed milestone
            setPendingMilestone(data.milestones[data.milestones.length - 1]);
          }
        }
      } catch { /* non-critical */ }
    };
    // Small delay so the page settles first
    const timer = setTimeout(checkMilestones, 2000);
    return () => clearTimeout(timer);
  }, [getUserId]);

  const rewardEssence = useCallback(async (action, multiplier = 1.0) => {
    const uid = getUserId();
    if (!uid) return null;

    // Debounce: skip if same action rewarded within 10 seconds
    const key = `${uid}:${action}`;
    const now = Date.now();
    if (recentRewards.has(key) && now - recentRewards.get(key) < 10000) return null;
    recentRewards.set(key, now);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_URL}/api/avatar/absorb-essence?user_id=${uid}&action=${action}&multiplier=${multiplier}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!res.ok) return null;
      const data = await res.json();

      // Check for milestone reached
      if (data.milestone_reached) {
        // Delay milestone modal slightly so the toast shows first
        setTimeout(() => setPendingMilestone(data.milestone_reached), 1500);
      }

      // Toast notification
      if (data.tier_up) {
        toast.success(data.message, { duration: 6000, className: 'essence-toast-tierup' });
      } else if (data.essence_awarded > 0) {
        toast(
          `+${data.essence_awarded} Brand Essence absorbed`,
          { duration: 3000, className: 'essence-toast' }
        );
      }

      return data;
    } catch {
      return null;
    }
  }, [getUserId]);

  const dismissMilestone = useCallback(() => {
    setPendingMilestone(null);
  }, []);

  return { rewardEssence, pendingMilestone, dismissMilestone };
}
